/**
 * Provider and Site Cleanup Types
 *
 * These types support the site cleanup integration features:
 * - NPI-based provider management
 * - Site proximity search
 * - Patient care team tracking
 * - Multi-phone/fax management for sites
 */

export interface Provider {
  id: number;
  npi?: string;
  prefix?: string;
  first_name: string;
  middle_name?: string;
  last_name: string;
  suffix?: string;
  credential?: string;
  specialty: string;
  taxonomy_code?: string;
  taxonomy_display?: string;
  direct_email?: string;
  display_name?: string;
  is_placeholder?: boolean;
  sites?: ProviderSiteInfo[];
}

export interface ProviderSiteInfo {
  id: number;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  phone?: string;
  fax?: string;
  is_primary: boolean;
  confirmed_at?: string;
}

export interface Site {
  id: number;
  name: string;
  address?: string;
  address_1?: string;  // Alias for address
  address_2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  postal_code?: string;  // Alias for zip_code
  phone?: string;
  phone_confirmed_at?: string;  // For confirmation status icon
  fax?: string;
  fax_confirmed_at?: string;    // For confirmation status icon
  email?: string;
  hours?: string;
  npi?: string;
  tests_offered?: string;
  is_placeholder?: boolean;
  placeholder_source?: string;  // 'npi_registry', 'surescripts', etc.
  comment?: string;  // Notes/comments about the site
  diagnostic_site_rating?: number | null;  // 1-5 star rating for diagnostic test sites
  tests_outreach_date?: string | null;     // ISO datetime when site was contacted
  tests_outreach_outcome?: string | null;  // confirmed, no_longer_offers, partial, no_response, unable_to_reach, pending
}

/**
 * Outreach outcome options for diagnostic test sites
 */
export const TESTS_OUTREACH_OUTCOMES = [
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'no_longer_offers', label: 'No Longer Offers Tests' },
  { value: 'partial', label: 'Partial (Some Tests Only)' },
  { value: 'no_response', label: 'No Response' },
  { value: 'unable_to_reach', label: 'Unable to Reach' },
  { value: 'pending', label: 'Pending' },
] as const;

export type TestsOutreachOutcome = typeof TESTS_OUTREACH_OUTCOMES[number]['value'];

/**
 * SitePhone - Multi-purpose phone/fax numbers for sites
 *
 * IMPORTANT: Use this instead of single phone/fax fields on Site.
 * - Each site can have multiple phones AND multiple faxes
 * - Confirmation is per-site-phone association (not global per number)
 * - Purpose can be comma-separated for multiple purposes
 */
export interface SitePhone {
  id: number;
  site_id: number;
  number: string;
  number_type: 'phone' | 'fax';
  purpose: string;  // comma-separated: general, send records to, scheduling, orders, record request, other
  confirmed_at?: string;
  confirmation_source?: string;  // 'manual', 'npi_registry', 'elation', etc.
  notes?: string;
  // Populated by backend when this number is shared with other sites
  shared_with?: Array<{ site_id: number; site_name: string }>;
}

/**
 * Phone purposes for SitePhone
 * Used in multi-select when adding/editing phone numbers
 */
export const PHONE_PURPOSES = [
  'general',
  'send records to',
  'scheduling',
  'orders',
  'record request',
  'other'
] as const;

export type PhonePurpose = typeof PHONE_PURPOSES[number];

/**
 * Patient care team - provider at a specific site with a role
 */
export interface PatientProviderSite {
  id: number;
  patient_id: number;
  provider_id: number;
  // Optional: a provider with no linked site is a legitimate care team member.
  site_id?: number;
  provider: Provider;
  site?: Site;
  role: CareTeamRole;
  is_primary: boolean;
  // Referring is a SECOND dimension, not a role — a cardiologist who referred the
  // patient is both, and `role` holds one value. Renders as a star, like is_primary.
  is_referring?: boolean;
  is_validated: boolean;
  validated_at?: string;
  notes?: string;
}

export type CareTeamRole =
  | 'pcp'
  | 'cardiology'
  | 'endocrinology'
  | 'nephrology'
  | 'rheumatology'
  | 'gastroenterology'
  | 'pulmonology'
  | 'surgery'
  | 'neurology'
  | 'hematology'
  | 'other';

export const CARE_TEAM_ROLES: { value: CareTeamRole; label: string }[] = [
  { value: 'pcp', label: 'Primary Care Provider' },
  { value: 'cardiology', label: 'Cardiologist' },
  { value: 'neurology', label: 'Neurologist' },
  { value: 'hematology', label: 'Hematologist'},
  { value: 'endocrinology', label: 'Endocrinologist' },
  { value: 'nephrology', label: 'Nephrologist' },
  { value: 'rheumatology', label: 'Rheumatologist' },
  { value: 'gastroenterology', label: 'Gastroenterologist' },
  { value: 'pulmonology', label: 'Pulmonologist' },
  { value: 'surgery', label: 'Surgeon' },
  { value: 'other', label: 'Other' },
];

/**
 * NPI Registry lookup result
 */
export interface NPILookupResult {
  npi: string;
  entity_type: 'individual' | 'organization';
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  credential?: string;
  organization_name?: string;
  taxonomy_code?: string;
  taxonomy_description?: string;
  all_specialties?: string;
  display_name?: string;
  address_1?: string;
  address_2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  phone?: string;
  fax?: string;
}

/**
 * Site proximity search result
 */
export interface ProximitySearchResult {
  site: Site;
  distance_miles: number;
  distance_display: string;
  matching_tests: string[];
}

export interface ProximitySearchParams {
  zipcode: string;
  radius_miles?: number;
  tests?: string[];
  require_all_tests?: boolean;
  exclude_placeholders?: boolean;
  limit?: number;
}

/**
 * Confirmation status helpers
 */
export type ConfirmationStatus = 'fresh' | 'stale' | 'never';

export function getConfirmationStatus(confirmedAt?: string, staleDays: number = 365): ConfirmationStatus {
  if (!confirmedAt) return 'never';

  const confirmedDate = new Date(confirmedAt);
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - staleDays);

  return confirmedDate >= cutoffDate ? 'fresh' : 'stale';
}

export function getConfirmationIcon(confirmedAt?: string, staleDays: number = 365): string {
  const status = getConfirmationStatus(confirmedAt, staleDays);
  switch (status) {
    case 'fresh': return '✓';  // Confirmed within last year
    case 'stale': return '○';  // Confirmed but older than 1 year
    case 'never': return '—';  // Never confirmed
  }
}

/**
 * Get MUI icon name for confirmation status
 */
export function getConfirmationMuiIcon(confirmedAt?: string, staleDays: number = 365): string {
  const status = getConfirmationStatus(confirmedAt, staleDays);
  switch (status) {
    case 'fresh': return 'CheckCircle';
    case 'stale': return 'RadioButtonUnchecked';
    case 'never': return 'Remove';
  }
}

export function getConfirmationColor(confirmedAt?: string, staleDays: number = 365): string {
  const status = getConfirmationStatus(confirmedAt, staleDays);
  switch (status) {
    case 'fresh': return 'success';
    case 'stale': return 'warning';
    case 'never': return 'disabled';
  }
}

/**
 * Pharmacy from Elation/NCPDP database
 */
export interface Pharmacy {
  id: number;
  elation_id?: number;
  ncpdp_id: string;  // Required for Elation integration
  store_name: string;
  address_1?: string;
  address_2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  phone?: string;
  fax?: string;
  npi?: string;
  specialty_types?: string;
  full_address?: string;
  formatted_phone?: string;
  is_retail?: boolean;
}

/**
 * Patient pharmacy association
 */
export interface PatientPharmacy {
  id: number;
  patient_id: number;
  pharmacy_id: number;
  pharmacy: Pharmacy;
  is_preferred: boolean;
  notes?: string;
  added_at?: string;
  source?: string;  // 'elation', 'manual', 'import'
}

/**
 * Format phone number for display
 */
export function formatPhone(phone?: string): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
}
